/**
 * Proxy Messaging & Calling Service — Backend-Routed
 *
 * Architecture:
 *   Driver types message in app → Backend API → OpenPhone → Customer receives SMS from proxy number
 *   Customer replies via SMS → OpenPhone webhook → Backend stores it → App fetches from backend
 *
 * The driver never sees the customer's real phone number.
 * The customer never sees the driver's real phone number.
 * Everything routes through the backend — no OpenPhone API key in the client.
 * The Commonplace proxy number (+1 516-357-5989) is managed server-side.
 */
import { api } from './api';

const OPENPHONE_PROXY_NUMBER = process.env.EXPO_PUBLIC_OPENPHONE_NUMBER || '+15163575989';

export type RecipientType = 'BUYER' | 'SELLER' | 'AGENT';

export interface ProxyMessage {
  id: string;
  jobId: string;
  messageText: string;
  // The driver's original typed text (before AI polish). Used to collapse an optimistic
  // bubble onto its stored server message even though the sent text was rewritten.
  originalMessageText?: string;
  senderType: 'DRIVER' | 'BUYER' | 'SELLER' | 'AGENT' | 'SYSTEM';
  recipientType: RecipientType;
  isInbound: boolean;
  timestamp: string;
  status?: 'SENT' | 'DELIVERED' | 'FAILED' | 'PENDING';
}

export interface CallRequest {
  jobId: string;
  recipientType: RecipientType;
  customerPhone: string;
}


export const proxyMessaging = {
  /**
   * Send a message to buyer, seller, or support agent through the backend.
   * Backend handles OpenPhone API calls — no API key in the client.
   *
   * @param customerPhone - Passed for backwards compatibility but backend resolves the number from the job
   */
  async sendMessage(
    jobId: string,
    recipientType: RecipientType,
    messageText: string,
    _customerPhone?: string,
    onBehalfOfDriverId?: string,
  ): Promise<ProxyMessage> {
    try {
      const body: any = { jobId, recipientType, messageText };
      // Fleet manager replying on behalf of one of their fleet drivers (the
      // job's assignedFleetDriverId). Backend validates and stores Message
      // with driverId = that fleet driver so the thread reads consistently.
      if (onBehalfOfDriverId) body.onBehalfOfDriverId = onBehalfOfDriverId;
      // 30s timeout (vs the 15s default): the backend polishes the message with AI before
      // sending, which can take several seconds. The default timeout could fire mid-polish →
      // the driver sees "failed" and resends, even though the message DID go out (duplicates).
      const { data } = await api.post('/messages/send', body, { timeout: 30000 });

      return {
        id: data?.id || `m-${Date.now()}`,
        jobId,
        // The stored/sent text is the AI-polished version — show the driver what the customer
        // actually received, plus the original they typed so the bubble can reconcile.
        messageText: data?.messageText || messageText,
        originalMessageText: data?.originalMessageText || messageText,
        senderType: 'DRIVER',
        recipientType,
        isInbound: false,
        timestamp: data?.timestamp || new Date().toISOString(),
        status: data?.deliveryStatus === 'failed' ? 'FAILED' : 'SENT',
      };
    } catch (err: any) {
      // ALWAYS throw so the screen's catch runs (marks the message FAILED + lets the
      // driver resend). The old code swallowed non-400 errors and returned a FAILED
      // object the screen ignored, so failed sends sat on "Sending" forever.
      const errorMsg = err?.response?.data?.error || err.message;
      console.warn('[Messaging] Send failed:', errorMsg);
      throw new Error(errorMsg || 'Message did not send — check your connection and try again.');
    }
  },

  /**
   * Get message thread for a job from the backend.
   * Backend stores all messages (OpenPhone webhook + driver-sent).
   *
   * @param customerPhone - Ignored; backend resolves from job
   */
  async getThread(
    jobId: string,
    recipientType: RecipientType,
    _customerPhone?: string,
  ): Promise<ProxyMessage[]> {
    try {
      const { data } = await api.get(`/messages/thread/${jobId}`, {
        params: { recipientType },
      });

      const messages: ProxyMessage[] = (data || []).map((msg: any) => ({
        id: msg.id,
        jobId: msg.jobId || jobId,
        messageText: msg.messageText || msg.content || msg.body || '',
        originalMessageText: msg.originalMessageText || undefined,
        senderType: msg.senderType || (msg.isInbound ? recipientType : 'DRIVER'),
        recipientType: msg.recipientType || recipientType,
        isInbound: msg.isInbound ?? msg.senderType !== 'DRIVER',
        timestamp: msg.timestamp || msg.createdAt || new Date().toISOString(),
        // Surface real delivery status when present (failed → show failed, not a fake DELIVERED).
        status: msg.deliveryStatus === 'failed' ? 'FAILED' : (msg.status || 'DELIVERED'),
      }));

      return messages.sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );
    } catch (err: any) {
      // THROW - never return []. An empty array is indistinguishable from "this thread has
      // no messages", so a timeout or a 500 used to render as an empty conversation with no
      // hint that anything had gone wrong. The screen decides what to show.
      const errorMsg = err?.response?.data?.error || err.message;
      console.warn('[Messaging] Get thread failed:', errorMsg);
      throw new Error(errorMsg || 'Could not load messages');
    }
  },

  /**
   * Initiate a proxy call.
   * Try to get a call routing token from backend; fall back to proxy number.
   * Customer sees the Commonplace proxy number on caller ID, not the driver's.
   */
  async initiateCall(
    jobId: string,
    recipientType: RecipientType,
  ): Promise<{ proxyNumber: string; status: string }> {
    try {
      const { data } = await api.post('/messages/call', { jobId, recipientType });
      return { proxyNumber: data?.proxyNumber || OPENPHONE_PROXY_NUMBER, status: data?.status || 'READY' };
    } catch {
      // Fallback: driver dials the proxy number directly
      return { proxyNumber: OPENPHONE_PROXY_NUMBER, status: 'READY' };
    }
  },

  /**
   * Send a quick template message through the backend
   */
  async sendQuickMessage(
    jobId: string,
    recipientType: RecipientType,
    templateKey: string,
    _customerPhone?: string,
  ): Promise<ProxyMessage> {
    const templates: Record<string, string> = {
      'on-my-way': "I'm on my way! I'll be there shortly.",
      'arrived': "I've arrived and I'm outside.",
      'running-late': "I'm running a few minutes behind. I'll be there soon.",
      'need-access': "Could you provide access instructions for the location?",
      'loading-complete': "Loading is complete. I'm heading to the delivery location now.",
      'eta-update': "Just a quick update. I should be there within the next 15 to 20 minutes.",
    };
    const messageText = templates[templateKey] || templateKey;
    return this.sendMessage(jobId, recipientType, messageText);
  },

  /**
   * Contact support agent — sends directly to backend (admin reads from DB).
   */
  async contactAgent(jobId: string, messageText: string): Promise<ProxyMessage> {
    return this.sendMessage(jobId, 'AGENT', messageText);
  },

  /**
   * Fetch support thread from backend (agent messages stored in DB).
   */
  async getAgentThread(jobId: string): Promise<ProxyMessage[]> {
    return this.getThread(jobId, 'AGENT');
  },
};

/** Quick message templates available to the driver */
export const QUICK_MESSAGES = [
  { key: 'on-my-way', label: "I'm on my way", icon: 'navigate' },
  { key: 'arrived', label: "I've arrived", icon: 'location' },
  { key: 'running-late', label: 'Running late', icon: 'time' },
  { key: 'need-access', label: 'Need access info', icon: 'key' },
  { key: 'loading-complete', label: 'Loading complete', icon: 'checkmark-circle' },
  { key: 'eta-update', label: 'ETA update', icon: 'speedometer' },
] as const;
